import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { formatNaira } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import { Constants } from '../../../utils/constants';
import PageHeader from '../../shell/PageHeader';
import { Button, Card, DataTable, DateRangeFilter, EmptyState, FilterSelect, Pagination, StatusPill, TableCard } from '../../ui';
import useClientTable from '../../../hooks/useClientTable';
import useDateRange from '../../../hooks/useDateRange';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Agent Transactions' }];

const buildColumns = ({ dateFilter, statusFilter }) => [
  { key: 'created_at', header: 'Date (WAT)', render: (row) => formatLagos(row.created_at), filter: dateFilter },
  { key: 'user', header: 'Customer', render: (row) => row.user || '—' },
  { key: 'reference', header: 'Reference', render: (row) => row.reference || '—' },
  { key: 'amount', header: 'Amount', align: 'right', render: (row) => formatNaira(row.amount) },
  { key: 'cash_tap_type', header: 'Type', className: 'capitalize', render: (row) => row.cash_tap_type || '—' },
  { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} />, filter: statusFilter },
];

const AgentTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAnAgent, setNotAnAgent] = useState(false);
  const [status, setStatus] = useState('');

  const { from, to, setFrom, setTo, invalid, filter } = useDateRange();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotAnAgent(false);
    try {
      const response = await api.get(PartnerEndpoints.agentTransactions);
      setTransactions(response.data?.data?.data ?? response.data?.data ?? []);
    } catch (err) {
      if (err.response?.status === 403) setNotAnAgent(true);
      else setError('Could not load your agent transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(
    () => filter(transactions).filter((row) => !status || row.status?.toLowerCase() === status),
    [filter, transactions, status]
  );

  const rangeTotal = useMemo(
    () => visibleRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [visibleRows]
  );

  const { page, setPage, pageSize, setPageSize, pageRows, pageCount } = useClientTable(visibleRows, {
    initialPageSize: 10,
  });

  if (notAnAgent) {
    return (
      <>
        <PageHeader title="Agent Transactions" breadcrumbs={breadcrumbs} />
        <Card>
          <EmptyState
            icon={<ShieldAlert className="h-10 w-10" aria-hidden="true" />}
            title="Not a CashTap agent"
            message="Your merchant account is not registered as a CashTap agent. Contact support if you believe this is an error."
            action={
              <Link to="/layout" className="btn btn-outline-primary">
                Go to dashboard
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Agent Transactions" breadcrumbs={breadcrumbs} />

      <TableCard
        title="CashTap transactions"
        summary={{ label: 'Total amount', value: formatNaira(rangeTotal), loading }}
        toolbar={
          error && (
            <Button size="sm" onClick={load}>
              Try again
            </Button>
          )
        }
        footer={
          visibleRows.length > 0 && (
            <Pagination
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={buildColumns({
            dateFilter: <DateRangeFilter label="Date" from={from} to={to} onFromChange={setFrom} onToChange={setTo} />,
            statusFilter: (
              <FilterSelect value={status} onChange={setStatus} options={Constants.agentTransactionStatuses} label="All" ariaLabel="Filter by status" />
            ),
          })}
          rows={pageRows}
          rowKey={(row) => row.id}
          serial
          serialStart={page * pageSize}
          loading={loading}
          error={error}
          invalidDateRange={invalid}
          empty={{
            title: 'No transactions',
            message: 'No CashTap transactions match the selected filters.',
          }}
        />
      </TableCard>
    </>
  );
};

export default AgentTransactions;
